import conciliacionService from '@/services/conciliacionService';

/**
 * Bandeja de casos de conciliación (`conciliacion-externa`, card #349,
 * design.md ADR-08 / UI-004). Copia deliberada de la forma de
 * `store/modules/pagosEgresos.js`: `state()` como función, `loading`/`error`
 * de la bandeja separados de `exportando`/`exportError` (exportar no puede
 * blanquear la tabla), y `extraerMensajeError` VERBATIM (pagosEgresos.js:71-77)
 * — no se importa desde ese módulo (no lo exporta y no es de este dominio
 * tocarlo) sino que se copia igual, según design.md ADR-08.
 *
 * `filtros`: `estado` acepta `'todos'` o una lista de estados separados por coma
 * (validaciones.js, reconciliado con WU5 — engram #825/#826); `id_cuenta`/`fecha_desde`/
 * `fecha_hasta`/`fuente`/`monto` confirmados contra `validarListarCasos`
 * (WU5, `routes/validaciones.js`) — ya no son una suposición.
 *
 * WU6b agrega: `importarMercadoPago` (POST, estado propio `importando`/`importError`/
 * `ultimaImportacion`), `fetchCaso`/`limpiarCasoDetalle` (detalle de un caso + candidatos para
 * `DrawerCaso.vue`) y las 4 resoluciones (`vincular`/`reimputar`/`crearMovimiento`/`ignorar`,
 * factory compartida `_crearAccionResolucion`, estado propio `resolviendo`/`resolverError`).
 */

const filtrosPorDefecto = () => ({
  estado: 'todos',
  id_cuenta: null,
  fecha_desde: null,
  fecha_hasta: null,
});

const paginacionPorDefecto = () => ({
  pagina_actual: 1,
  total_paginas: 1,
  total_registros: 0,
  registros_por_pagina: 50,
});

const resumenPorDefecto = () => ({
  total_lineas: 0,
  total_casos: 0,
  pendientes: 0,
  ambiguos: 0,
  sin_correspondencia: 0,
  vinculados: 0,
  reimputados: 0,
  creados: 0,
  ignorados: 0,
  monto_externo: 0,
  monto_sistema: 0,
  diferencia: 0,
});

const state = () => ({
  items: [],
  headers: [],
  paginacion: paginacionPorDefecto(),
  resumen: resumenPorDefecto(),
  filtros: filtrosPorDefecto(),
  filtrosAplicados: {},
  loading: false,
  error: null,
  exportando: false,
  exportError: null,
  // WU6b — importación (PanelImportacion.vue): estado propio, no pisa loading/error de la
  // bandeja (mismo criterio que exportando/exportError, UI-004).
  importando: false,
  importError: null,
  ultimaImportacion: null,
  // WU6b — detalle de un caso + candidatos (DrawerCaso.vue).
  casoDetalle: null,
  casoDetalleLoading: false,
  casoDetalleError: null,
  // WU6b — las 4 resoluciones (vincular/reimputar/crearMovimiento/ignorar) comparten un
  // único estado de loading/error: son mutuamente excluyentes (un caso, un diálogo a la vez).
  resolviendo: false,
  resolverError: null,
  // WU6c — defaults de matching por tenant (GET|PUT /conciliacion/config, CFG-002). Estado
  // propio: no pisa loading/error de la bandeja ni de las resoluciones.
  config: null,
  configLoading: false,
  configError: null,
  actualizandoConfig: false,
  actualizarConfigError: null,
  // WU8 — pertenencia por sucursal/caja de Mercado Pago. Dos tenants comparten una cuenta de
  // MP; cada uno declara cuáles sucursales/cajas son propias. `puntosVentaMP` = catálogo LIVE
  // (no persistido) traído de MP; `puntosVentaConfig` = lo guardado para la cuenta seleccionada.
  // Estado propio, no pisa `config`/`parametrosAcreditacion` (WU6c).
  puntosVentaMP: [],
  puntosVentaMPLoading: false,
  puntosVentaMPError: null,
  puntosVentaConfig: [],
  // `id_cuenta` de la solicitud EN CURSO/más reciente de `fetchPuntosVentaConfig` (JD ronda 1
  // issue #1). Se sigue guardando (lo usa el componente para saber a qué cuenta corresponde
  // la config vigente), pero YA NO es el guard de concurrencia: un guard por VALOR de
  // id_cuenta no distingue A→B→A (dos solicitudes distintas a la MISMA cuenta) — la primera,
  // stale, tiene el mismo id_cuenta que la vigente y pasaría el guard igual (JD ronda 2 issue
  // #1). El guard real es `puntosVentaConfigRequestId`.
  puntosVentaConfigCuenta: null,
  // Token monotónico de la solicitud EN CURSO/más reciente, COMPARTIDO por
  // `fetchPuntosVentaConfig` y `guardarPuntosVentaConfig` (JD ronda 2 issue #1: un save de A
  // resolviendo después de que el usuario cambió a B tampoco debe pisar la config de B — no
  // sólo el fetch necesita el guard). Cada acción hace `token = puntosVentaConfigRequestId + 1`
  // y sólo commitea su resultado si su `token` sigue siendo el vigente al resolver.
  puntosVentaConfigRequestId: 0,
  // JD ronda 4 issue #1: `puntosVentaConfigLoading`/`guardandoPuntosVentaConfig` son booleanos
  // DERIVADOS de un contador de solicitudes pendientes (`puntosVentaConfigFetchPendientes` /
  // `guardarPuntosVentaConfigPendientes`) — un solo booleano compartido no distingue "la
  // solicitud que terminó era la única en vuelo" de "hay una más nueva todavía pendiente": una
  // solicitud stale resolviendo en su `finally` pisaba el flag a `false` mientras la más nueva
  // seguía en curso (ventana de doble guardado sin loading, reabre CRITICAL ronda 2). Las
  // mutaciones INC_/DEC_ son las únicas que tocan el booleano derivado; no se commitea el
  // booleano directamente desde las acciones.
  puntosVentaConfigFetchPendientes: 0,
  puntosVentaConfigLoading: false,
  puntosVentaConfigError: null,
  guardarPuntosVentaConfigPendientes: 0,
  guardandoPuntosVentaConfig: false,
  guardarPuntosVentaConfigError: null,
  // WU8 — líneas externas fuera de alcance (no pertenecen a ningún punto de venta
  // configurado como propio) y re-evaluación de un lote ya importado. Paginación server-side,
  // mismo shape que `paginacion` de la bandeja de casos.
  lineasFueraAlcance: [],
  lineasFueraAlcancePaginacion: paginacionPorDefecto(),
  lineasFueraAlcanceLoading: false,
  lineasFueraAlcanceError: null,
  reevaluando: false,
  reevaluarError: null,
  // WU8 — historial de lotes de importación (`GET /conciliacion/importaciones`), usado por
  // `PanelImportacion.vue` para mostrar `lineas_fuera_alcance` por lote con acceso a
  // `ListaFueraAlcance.vue`. El service ya existía desde WU6a (scaffold sin uso); WU8 es la
  // primera vez que se dispatchea.
  importaciones: [],
  importacionesLoading: false,
  importacionesError: null,
});

/**
 * `GET /conciliacion/casos` devuelve 400 en las mismas 3 formas que
 * `GET /egresos/pagos` (design.md ADR-08, copiado verbatim de
 * pagosEgresos.js:71-77): `{errors:[{msg}]}` de express-validator,
 * `{message}` de negocio, `{msg}` (p.ej. export vacío o gate DECIMAL).
 */
const extraerMensajeError = (error, mensajePorDefecto) => {
  const data = error.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.map((e) => e.msg || e.message).join(', ');
  }
  return data?.message || data?.msg || error.message || mensajePorDefecto;
};

/**
 * Factory compartido por las 4 resoluciones (T6b.4/T6b.6, RES-001 a RES-004): todas comparten
 * la misma forma — `{idCaso, ...body}` -> `conciliacionService[metodoServicio](idCaso, body)` —,
 * el mismo loading/error (`resolviendo`/`resolverError`) y el mismo manejo de errores 400/404/409
 * (extraerMensajeError normaliza los 3 shapes conocidos; el mapeo de status a UI — toast +
 * refresh vs. mensaje inline — vive en el componente, no acá).
 */
const _crearAccionResolucion = (metodoServicio, mensajeErrorPorDefecto) => async ({ commit }, { idCaso, ...body }) => {
  try {
    commit('SET_RESOLVIENDO', true);
    commit('SET_RESOLVER_ERROR', null);
    const { data } = await conciliacionService[metodoServicio](idCaso, body);
    return data.data;
  } catch (error) {
    commit('SET_RESOLVER_ERROR', extraerMensajeError(error, mensajeErrorPorDefecto));
    throw error;
  } finally {
    commit('SET_RESOLVIENDO', false);
  }
};

// Task 1 (#354): única Cuenta activa — ver comentario de la acción `setCuentaActiva` para el
// porqué de reusar `filtros.id_cuenta` en vez de un campo de estado separado.
const getters = {
  cuentaActivaId: (state) => state.filtros.id_cuenta,
  cuentaActiva: (state, _getters, _rootState, rootGetters) => {
    const cuentas = rootGetters['cuentasEmpresa/cuentasActivas'] || [];
    return cuentas.find((c) => c.id_cuenta === state.filtros.id_cuenta) || null;
  },
};

const actions = {
  async fetchCasos({ commit, state }, opciones = {}) {
    const { page = 1, limit = 50, ...filtrosOverride } = opciones;
    const filtros = { ...state.filtros, ...filtrosOverride };
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      const { data } = await conciliacionService.obtenerCasos({ ...filtros, page, limit });
      commit('SET_ITEMS', data.data || []);
      commit('SET_HEADERS', data.headers || []);
      commit('SET_PAGINACION', data.paginacion || paginacionPorDefecto());
      commit('SET_RESUMEN', data.resumen || resumenPorDefecto());
      commit('SET_FILTROS_APLICADOS', data.filtros_aplicados || {});
      commit('SET_FILTROS', filtros);
      return data;
    } catch (error) {
      // No dejar la bandeja/resumen anteriores (de un fetch previo exitoso) mostrados
      // debajo del banner de error — no toca `filtros`, el usuario debe poder corregirlos.
      commit('SET_ITEMS', []);
      commit('SET_PAGINACION', paginacionPorDefecto());
      commit('SET_RESUMEN', resumenPorDefecto());
      commit('SET_ERROR', extraerMensajeError(error, 'Error al cargar la bandeja de conciliación'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Export ignora la paginación server-side (todos los registros, no sólo
   * la página actual) — `page`/`limit` se descartan antes de llamar al
   * servicio.
   */
  async exportarCasos({ commit, state }, opciones = {}) {
    const filtrosOverride = { ...opciones };
    delete filtrosOverride.page;
    delete filtrosOverride.limit;
    const filtros = { ...state.filtros, ...filtrosOverride };
    try {
      commit('SET_EXPORTANDO', true);
      commit('SET_EXPORT_ERROR', null);
      const { data } = await conciliacionService.exportarCasos(filtros);
      return data;
    } catch (error) {
      commit('SET_EXPORT_ERROR', extraerMensajeError(error, 'Error al exportar la bandeja de conciliación'));
      throw error;
    } finally {
      commit('SET_EXPORTANDO', false);
    }
  },

  actualizarFiltros({ commit, state }, filtrosParciales = {}) {
    commit('SET_FILTROS', { ...state.filtros, ...filtrosParciales });
  },

  resetFiltros({ commit }) {
    commit('RESET_FILTROS');
  },

  /**
   * Task 1 (#354) — ÚNICA Cuenta activa para toda la pantalla. Antes de esta unidad había 4
   * selecciones de Cuenta independientes en la misma pantalla (`PuestaAPuntoStrip`, la barra de
   * filtros, `PanelImportacion`, `PanelPuntosVentaMP`): elegir una en un lado dejaba a los otros
   * tres mostrando otra Cuenta, contradiciendo el encabezado del diseño aprobado
   * ("Cuenta activa: MercadoPago · Local Brico Mayorista").
   *
   * Se unifica sobre `state.filtros.id_cuenta` (ya existía, ya es lo que `fetchCasos` manda como
   * filtro) en vez de agregar un campo de estado nuevo: dos campos separados para "la Cuenta
   * activa" y "el filtro de Cuenta de la bandeja" podrían desincronizarse entre sí, que es
   * exactamente el bug que esta unidad corrige. `cuentaActivaId`/`cuentaActiva` (getters) le dan
   * un nombre semántico al mismo valor para que los 4 consumidores no necesiten saber que vive
   * bajo `filtros`.
   *
   * Qué se invalida al cambiar de Cuenta, y por qué:
   *  - `items`/`paginacion`/`resumen` (bandeja de casos): pertenecen a la Cuenta anterior
   *    (`fetchCasos` filtra por `id_cuenta`) — dejarlos mostrados sería mezclar datos de dos
   *    Cuentas distintas en la misma tabla.
   *  - `puntosVentaConfig`/`puntosVentaConfigCuenta` (WU8): server-scoped por `id_cuenta`
   *    (`GET /conciliacion/mp/puntos-venta/config?id_cuenta=`) — se limpian y se vuelve a pedir
   *    para la Cuenta nueva.
   * Qué NO se invalida, y por qué:
   *  - `puntosVentaMP` (catálogo LIVE de sucursales/cajas de Mercado Pago): el adaptador de
   *    importación usa una credencial de Mercado Pago GLOBAL por tenant, no resuelta por
   *    `id_cuenta` (`cuentaPuedeImportarMP`, WU6b) — es el mismo catálogo sin importar cuál
   *    Cuenta interna esté activa.
   *  - `importaciones` (historial de lotes): ningún consumidor actual lo filtra por `id_cuenta`.
   *
   * El fetch de `puntosVentaConfig` para la Cuenta nueva se dispara ACÁ (una sola vez, desde la
   * única acción que cambia la Cuenta activa) en vez de que cada uno de los 4 consumidores lo
   * dispare por su cuenta al reaccionar a un getter compartido — eso duplicaría la solicitud de
   * red cada vez que cambia la Cuenta.
   */
  setCuentaActiva({ commit, dispatch }, idCuenta) {
    commit('SET_CUENTA_ACTIVA', idCuenta);
    commit('SET_ITEMS', []);
    commit('SET_PAGINACION', paginacionPorDefecto());
    commit('SET_RESUMEN', resumenPorDefecto());
    commit('SET_PUNTOS_VENTA_CONFIG', []);
    commit('SET_PUNTOS_VENTA_CONFIG_CUENTA', null);
    if (idCuenta !== null && idCuenta !== undefined) {
      // No se espera (`await`): quien cambia la Cuenta activa no debe bloquearse por esto: el
      // error, si lo hay, ya queda en `puntosVentaConfigError` (manejado por
      // `fetchPuntosVentaConfig`), no hace falta un segundo manejo acá.
      dispatch('fetchPuntosVentaConfig', idCuenta).catch(() => {});
    }
  },

  /**
   * Dispara `POST /conciliacion/importar/mercadopago` (T6b.2, IMP-001/IMP-004 front). No toca
   * el loading/error de la bandeja — mismo criterio que `exportarCasos`.
   */
  async importarMercadoPago({ commit }, { id_cuenta, fecha_desde, fecha_hasta }) {
    try {
      commit('SET_IMPORTANDO', true);
      commit('SET_IMPORT_ERROR', null);
      const { data } = await conciliacionService.importarMercadoPago({ id_cuenta, fecha_desde, fecha_hasta });
      commit('SET_ULTIMA_IMPORTACION', data.data);
      return data.data;
    } catch (error) {
      commit('SET_IMPORT_ERROR', extraerMensajeError(error, 'Error al importar el extracto'));
      throw error;
    } finally {
      commit('SET_IMPORTANDO', false);
    }
  },

  /** Detalle de un caso + candidatos (T6b.4, `DrawerCaso.vue`). */
  async fetchCaso({ commit }, idCaso) {
    try {
      commit('SET_CASO_DETALLE_LOADING', true);
      commit('SET_CASO_DETALLE_ERROR', null);
      const { data } = await conciliacionService.obtenerCaso(idCaso);
      commit('SET_CASO_DETALLE', data.data);
      return data.data;
    } catch (error) {
      commit('SET_CASO_DETALLE_ERROR', extraerMensajeError(error, 'Error al obtener el caso'));
      throw error;
    } finally {
      commit('SET_CASO_DETALLE_LOADING', false);
    }
  },

  limpiarCasoDetalle({ commit }) {
    commit('CLEAR_CASO_DETALLE');
  },

  vincular: _crearAccionResolucion('vincular', 'Error al vincular el caso'),
  reimputar: _crearAccionResolucion('reimputar', 'Error al reimputar el caso'),
  crearMovimiento: _crearAccionResolucion('crearMovimiento', 'Error al registrar el movimiento'),
  ignorar: _crearAccionResolucion('ignorar', 'Error al ignorar el caso'),

  /** Defaults de matching por tenant (T6c, CFG-002). */
  async fetchConfig({ commit }) {
    try {
      commit('SET_CONFIG_LOADING', true);
      commit('SET_CONFIG_ERROR', null);
      const { data } = await conciliacionService.obtenerConfig();
      commit('SET_CONFIG', data.data);
      return data.data;
    } catch (error) {
      commit('SET_CONFIG_ERROR', extraerMensajeError(error, 'Error al cargar la configuración'));
      throw error;
    } finally {
      commit('SET_CONFIG_LOADING', false);
    }
  },

  async actualizarConfigDefaults({ commit }, datos) {
    try {
      commit('SET_ACTUALIZANDO_CONFIG', true);
      commit('SET_ACTUALIZAR_CONFIG_ERROR', null);
      const { data } = await conciliacionService.actualizarConfig(datos);
      commit('SET_CONFIG', data.data);
      return data.data;
    } catch (error) {
      commit('SET_ACTUALIZAR_CONFIG_ERROR', extraerMensajeError(error, 'Error al actualizar la configuración'));
      throw error;
    } finally {
      commit('SET_ACTUALIZANDO_CONFIG', false);
    }
  },

  /** Catálogo LIVE de sucursales/cajas de la cuenta de Mercado Pago compartida (T8f, WU8). */
  async fetchPuntosVentaMP({ commit }) {
    try {
      commit('SET_PUNTOS_VENTA_MP_LOADING', true);
      commit('SET_PUNTOS_VENTA_MP_ERROR', null);
      const { data } = await conciliacionService.listarPuntosVentaMP();
      commit('SET_PUNTOS_VENTA_MP', data.data || []);
      return data.data;
    } catch (error) {
      commit('SET_PUNTOS_VENTA_MP_ERROR', extraerMensajeError(error, 'Error al traer sucursales y cajas de Mercado Pago'));
      throw error;
    } finally {
      commit('SET_PUNTOS_VENTA_MP_LOADING', false);
    }
  },

  /**
   * Lo ya configurado para una cuenta (preselección del panel, T8f). Guarda de solicitud
   * fuera de orden (JD ronda 1 issue #1, reforzado en ronda 2 issue #1): un guard por VALOR de
   * `id_cuenta` no cubre A -> B -> A (dos solicitudes distintas a la MISMA cuenta A: la
   * primera, stale, comparte id_cuenta con la vigente y pisaría igual). Se usa en cambio un
   * token monotónico (`puntosVentaConfigRequestId`, compartido con `guardarPuntosVentaConfig`):
   * cada llamada toma el próximo número y sólo commitea su resultado si sigue siendo el token
   * vigente al resolver. `puntosVentaConfigCuenta` se sigue registrando (lo usa el componente),
   * pero ya no es el guard.
   */
  async fetchPuntosVentaConfig({ commit, state }, id_cuenta) {
    const token = state.puntosVentaConfigRequestId + 1;
    commit('SET_PUNTOS_VENTA_CONFIG_REQUEST_ID', token);
    commit('SET_PUNTOS_VENTA_CONFIG_CUENTA', id_cuenta);
    try {
      commit('INC_PUNTOS_VENTA_CONFIG_FETCH_PENDIENTES');
      commit('SET_PUNTOS_VENTA_CONFIG_ERROR', null);
      const { data } = await conciliacionService.obtenerPuntosVentaConfig(id_cuenta);
      if (token === state.puntosVentaConfigRequestId) {
        commit('SET_PUNTOS_VENTA_CONFIG', data.data || []);
      }
      return data.data;
    } catch (error) {
      if (token === state.puntosVentaConfigRequestId) {
        commit('SET_PUNTOS_VENTA_CONFIG', []);
        commit('SET_PUNTOS_VENTA_CONFIG_ERROR', extraerMensajeError(error, 'Error al obtener la configuración de puntos de venta'));
      }
      throw error;
    } finally {
      // JD ronda 3 issue #1 (mantenido) + ronda 4 issue #1: el token sólo guarda los commits de
      // datos/error de arriba — el contador de pendientes SIEMPRE se decrementa acá, así una
      // solicitud stale nunca deja el booleano derivado colgado en `true` NI lo pisa a `false`
      // mientras otra más nueva sigue en vuelo (cada solicitud sólo resta lo que sumó).
      commit('DEC_PUNTOS_VENTA_CONFIG_FETCH_PENDIENTES');
    }
  },

  /**
   * Reemplaza el set completo de puntos de venta propios de la cuenta (T8f). Comparte el
   * mismo token monotónico que `fetchPuntosVentaConfig` (JD ronda 2 issue #1): si el usuario
   * cambia de cuenta mientras un guardado de la cuenta anterior sigue en vuelo, esa respuesta
   * tardía NUNCA debe pisar la config ya cargada de la cuenta nueva.
   */
  async guardarPuntosVentaConfig({ commit, state }, { id_cuenta, puntos }) {
    const token = state.puntosVentaConfigRequestId + 1;
    commit('SET_PUNTOS_VENTA_CONFIG_REQUEST_ID', token);
    try {
      commit('INC_GUARDAR_PUNTOS_VENTA_CONFIG_PENDIENTES');
      commit('SET_GUARDAR_PUNTOS_VENTA_CONFIG_ERROR', null);
      const { data } = await conciliacionService.guardarPuntosVentaConfig({ id_cuenta, puntos });
      if (token === state.puntosVentaConfigRequestId) {
        commit('SET_PUNTOS_VENTA_CONFIG', data.data || []);
      }
      return data.data;
    } catch (error) {
      if (token === state.puntosVentaConfigRequestId) {
        commit('SET_GUARDAR_PUNTOS_VENTA_CONFIG_ERROR', extraerMensajeError(error, 'Error al guardar la configuración de puntos de venta'));
      }
      throw error;
    } finally {
      // JD ronda 3 issue #1 (mantenido) + ronda 4 issue #1: mismo criterio que
      // fetchPuntosVentaConfig — el token guarda sólo el commit de datos/error, el contador de
      // "guardando" pendientes SIEMPRE se decrementa acá (nunca pisa a `false` un guardado más
      // nuevo que sigue en vuelo).
      commit('DEC_GUARDAR_PUNTOS_VENTA_CONFIG_PENDIENTES');
    }
  },

  /**
   * Líneas fuera de alcance (`alcance=fuera_alcance` siempre agregado acá, T8f — el llamador
   * nunca decide el valor de `alcance`). Acepta cualquier filtro soportado por
   * `GET /conciliacion/lineas`, incluido `id_importacion` (JD ronda 1 issue #4 — correlato
   * exacto del lote, en vez de `id_cuenta`+fechas que mezclaba lotes solapados): se pasa tal
   * cual dentro de `opciones`, no hace falta declararlo acá. Mismo criterio de
   * reset-on-failure que `fetchCasos`: no deja la tabla anterior mostrada debajo de un error
   * nuevo.
   */
  async fetchLineasFueraAlcance({ commit }, opciones = {}) {
    const { page = 1, limit = 50, ...filtros } = opciones;
    try {
      commit('SET_LINEAS_FUERA_ALCANCE_LOADING', true);
      commit('SET_LINEAS_FUERA_ALCANCE_ERROR', null);
      const { data } = await conciliacionService.listarLineas({ ...filtros, alcance: 'fuera_alcance', page, limit });
      commit('SET_LINEAS_FUERA_ALCANCE', data.data || []);
      commit('SET_LINEAS_FUERA_ALCANCE_PAGINACION', data.paginacion || paginacionPorDefecto());
      return data;
    } catch (error) {
      commit('SET_LINEAS_FUERA_ALCANCE', []);
      commit('SET_LINEAS_FUERA_ALCANCE_PAGINACION', paginacionPorDefecto());
      commit('SET_LINEAS_FUERA_ALCANCE_ERROR', extraerMensajeError(error, 'Error al obtener las líneas fuera de alcance'));
      throw error;
    } finally {
      commit('SET_LINEAS_FUERA_ALCANCE_LOADING', false);
    }
  },

  /** Re-evalúa el alcance de un lote ya importado contra la config ACTUAL de puntos de venta. */
  async reevaluarAlcance({ commit }, idImportacion) {
    try {
      commit('SET_REEVALUANDO', true);
      commit('SET_REEVALUAR_ERROR', null);
      const { data } = await conciliacionService.reevaluarAlcance(idImportacion);
      return data.data;
    } catch (error) {
      commit('SET_REEVALUAR_ERROR', extraerMensajeError(error, 'Error al re-evaluar el alcance'));
      throw error;
    } finally {
      commit('SET_REEVALUANDO', false);
    }
  },

  /** Historial de lotes de importación, con `lineas_fuera_alcance` por lote (T8f). */
  async fetchImportaciones({ commit }, params = {}) {
    try {
      commit('SET_IMPORTACIONES_LOADING', true);
      commit('SET_IMPORTACIONES_ERROR', null);
      const { data } = await conciliacionService.obtenerImportaciones(params);
      commit('SET_IMPORTACIONES', data.data || []);
      return data.data;
    } catch (error) {
      commit('SET_IMPORTACIONES_ERROR', extraerMensajeError(error, 'Error al obtener el historial de importaciones'));
      throw error;
    } finally {
      commit('SET_IMPORTACIONES_LOADING', false);
    }
  },
};

const mutations = {
  SET_ITEMS(s, v) { s.items = v; },
  SET_HEADERS(s, v) { s.headers = v; },
  SET_PAGINACION(s, v) { s.paginacion = v; },
  SET_RESUMEN(s, v) { s.resumen = v; },
  SET_FILTROS_APLICADOS(s, v) { s.filtrosAplicados = v; },
  SET_FILTROS(s, v) { s.filtros = v; },
  // Task 1 (#354): preserva `id_cuenta` — es la Cuenta activa de TODA la pantalla, no un filtro
  // más de la bandeja. "Limpiar" reinicia estado/fechas, nunca deselecciona la Cuenta activa.
  RESET_FILTROS(s) { s.filtros = { ...filtrosPorDefecto(), id_cuenta: s.filtros.id_cuenta }; },
  // Task 1 (#354): única Cuenta activa — ver comentario de la acción `setCuentaActiva`.
  SET_CUENTA_ACTIVA(s, v) { s.filtros = { ...s.filtros, id_cuenta: v }; },
  SET_LOADING(s, v) { s.loading = v; },
  SET_ERROR(s, v) { s.error = v; },
  SET_EXPORTANDO(s, v) { s.exportando = v; },
  SET_EXPORT_ERROR(s, v) { s.exportError = v; },
  SET_IMPORTANDO(s, v) { s.importando = v; },
  SET_IMPORT_ERROR(s, v) { s.importError = v; },
  SET_ULTIMA_IMPORTACION(s, v) { s.ultimaImportacion = v; },
  SET_CASO_DETALLE(s, v) { s.casoDetalle = v; },
  SET_CASO_DETALLE_LOADING(s, v) { s.casoDetalleLoading = v; },
  SET_CASO_DETALLE_ERROR(s, v) { s.casoDetalleError = v; },
  CLEAR_CASO_DETALLE(s) { s.casoDetalle = null; s.casoDetalleError = null; },
  SET_RESOLVIENDO(s, v) { s.resolviendo = v; },
  SET_RESOLVER_ERROR(s, v) { s.resolverError = v; },
  SET_CONFIG(s, v) { s.config = v; },
  SET_CONFIG_LOADING(s, v) { s.configLoading = v; },
  SET_CONFIG_ERROR(s, v) { s.configError = v; },
  SET_ACTUALIZANDO_CONFIG(s, v) { s.actualizandoConfig = v; },
  SET_ACTUALIZAR_CONFIG_ERROR(s, v) { s.actualizarConfigError = v; },
  SET_PUNTOS_VENTA_MP(s, v) { s.puntosVentaMP = v; },
  SET_PUNTOS_VENTA_MP_LOADING(s, v) { s.puntosVentaMPLoading = v; },
  SET_PUNTOS_VENTA_MP_ERROR(s, v) { s.puntosVentaMPError = v; },
  SET_PUNTOS_VENTA_CONFIG(s, v) { s.puntosVentaConfig = v; },
  SET_PUNTOS_VENTA_CONFIG_CUENTA(s, v) { s.puntosVentaConfigCuenta = v; },
  SET_PUNTOS_VENTA_CONFIG_REQUEST_ID(s, v) { s.puntosVentaConfigRequestId = v; },
  // JD ronda 4 issue #1: contador de solicitudes de fetch pendientes — el booleano derivado
  // (`puntosVentaConfigLoading`) sólo puede tocarse acá, nunca directamente desde una acción.
  // Clamp en 0: un DEC de más (no debería pasar, pero defensivo) no lo manda a negativo.
  INC_PUNTOS_VENTA_CONFIG_FETCH_PENDIENTES(s) {
    s.puntosVentaConfigFetchPendientes += 1;
    s.puntosVentaConfigLoading = s.puntosVentaConfigFetchPendientes > 0;
  },
  DEC_PUNTOS_VENTA_CONFIG_FETCH_PENDIENTES(s) {
    s.puntosVentaConfigFetchPendientes = Math.max(0, s.puntosVentaConfigFetchPendientes - 1);
    s.puntosVentaConfigLoading = s.puntosVentaConfigFetchPendientes > 0;
  },
  SET_PUNTOS_VENTA_CONFIG_ERROR(s, v) { s.puntosVentaConfigError = v; },
  // JD ronda 4 issue #1 (mirror): mismo contador para `guardarPuntosVentaConfig`.
  INC_GUARDAR_PUNTOS_VENTA_CONFIG_PENDIENTES(s) {
    s.guardarPuntosVentaConfigPendientes += 1;
    s.guardandoPuntosVentaConfig = s.guardarPuntosVentaConfigPendientes > 0;
  },
  DEC_GUARDAR_PUNTOS_VENTA_CONFIG_PENDIENTES(s) {
    s.guardarPuntosVentaConfigPendientes = Math.max(0, s.guardarPuntosVentaConfigPendientes - 1);
    s.guardandoPuntosVentaConfig = s.guardarPuntosVentaConfigPendientes > 0;
  },
  SET_GUARDAR_PUNTOS_VENTA_CONFIG_ERROR(s, v) { s.guardarPuntosVentaConfigError = v; },
  SET_LINEAS_FUERA_ALCANCE(s, v) { s.lineasFueraAlcance = v; },
  SET_LINEAS_FUERA_ALCANCE_PAGINACION(s, v) { s.lineasFueraAlcancePaginacion = v; },
  SET_LINEAS_FUERA_ALCANCE_LOADING(s, v) { s.lineasFueraAlcanceLoading = v; },
  SET_LINEAS_FUERA_ALCANCE_ERROR(s, v) { s.lineasFueraAlcanceError = v; },
  SET_REEVALUANDO(s, v) { s.reevaluando = v; },
  SET_REEVALUAR_ERROR(s, v) { s.reevaluarError = v; },
  SET_IMPORTACIONES(s, v) { s.importaciones = v; },
  SET_IMPORTACIONES_LOADING(s, v) { s.importacionesLoading = v; },
  SET_IMPORTACIONES_ERROR(s, v) { s.importacionesError = v; },
};

export default { namespaced: true, state, getters, actions, mutations };
